import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, ExternalLink, FileWarning, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchDocumentBlob } from '@/utils/documents';

export default function DocumentViewer() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const src = params.get('src') || '';
  const name = params.get('name') || 'documento.pdf';
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    if (!src) { setState({ status: 'error', message: t('docViewer.missing') }); return undefined; }
    let url;
    fetchDocumentBlob(src)
      .then(({ blob, type }) => {
        const mime = type || blob.type || 'application/pdf';
        url = URL.createObjectURL(mime.includes('pdf') || mime.startsWith('image/') ? blob : new Blob([blob], { type: 'application/pdf' }));
        setState({ status: 'ok', url, mime });
      })
      .catch((e) => setState({ status: 'error', message: e.message }));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/'));

  return (
    <main className="min-h-screen bg-slate-100 flex flex-col" data-testid="document-viewer">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={goBack} data-testid="doc-back"><ArrowLeft className="h-4 w-4 mr-1" />{t('docViewer.back')}</Button>
        <h1 className="flex-1 min-w-0 truncate text-sm font-semibold text-slate-900" data-testid="doc-name">{name}</h1>
        {state.status === 'ok' && (
          <>
            <a href={state.url} download={name} className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-700" data-testid="doc-download">
              <Download className="h-4 w-4" />{t('docViewer.download')}
            </a>
            <a href={state.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-slate-300 text-sm font-medium hover:bg-slate-50" data-testid="doc-open-new">
              <ExternalLink className="h-4 w-4" />{t('docViewer.openNew')}
            </a>
          </>
        )}
      </header>

      <section className="flex-1 flex items-stretch justify-center p-2 sm:p-4">
        {state.status === 'loading' && (
          <div className="m-auto flex flex-col items-center gap-3 text-slate-600" data-testid="doc-loading">
            <Loader2 className="h-8 w-8 animate-spin" /><p className="text-sm">{t('docViewer.loading')}</p>
          </div>
        )}
        {state.status === 'error' && (
          <div className="m-auto max-w-md text-center bg-white rounded-xl border border-rose-200 p-8" data-testid="doc-error">
            <FileWarning className="h-10 w-10 text-rose-600 mx-auto mb-3" />
            <p className="font-semibold text-slate-900 mb-1">{t('docViewer.errorTitle')}</p>
            <p className="text-sm text-slate-600">{state.message}</p>
            <Button className="mt-5" variant="outline" onClick={goBack}>{t('docViewer.back')}</Button>
          </div>
        )}
        {state.status === 'ok' && state.mime.startsWith('image/') && (
          <img src={state.url} alt={name} className="m-auto max-w-full max-h-[85vh] rounded-lg shadow" data-testid="doc-image" />
        )}
        {state.status === 'ok' && !state.mime.startsWith('image/') && (
          <iframe title={name} src={state.url} className="w-full max-w-5xl min-h-[80vh] rounded-lg shadow bg-white" data-testid="doc-frame" />
        )}
      </section>
    </main>
  );
}
